import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import { useLang } from "@/lib/LanguageContext";
import { SERVICE_IMG } from "@/lib/images";
import PageShell from "@/components/PageShell";
import ImageHero from "@/components/ImageHero";
import ImageCard from "@/components/ImageCard";
import Seo, { breadcrumbSchema } from "@/components/Seo";

export default function ServicesPage() {
  const { t, lang } = useLang();
  const sp = t.servicesPage;

  const crumbs = [
    { label: lang === "fr" ? "Accueil" : "Home", to: "/" },
    { label: "Services", to: "/services" },
  ];

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Book & Smile Services",
      itemListElement: t.services.items.map((s, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: s.title,
        url: `https://bookandsmiles.com${s.route}`,
      })),
    },
    breadcrumbSchema(crumbs),
  ];

  return (
    <PageShell>
      <Seo title={sp.seoTitle} description={sp.seoDesc} schema={schema} />

      <ImageHero testId="services-hero" image="opera" eyebrow={sp.eyebrow} titleA={sp.title} titleB={sp.titleB} text={sp.intro} crumbs={crumbs} />

      <section data-testid="services-grid" className="py-16 sm:py-32 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-16">
          {t.services.items.map((s) => (
            <ImageCard
              key={s.id}
              to={s.route}
              testId={`services-page-${s.id}`}
              image={SERVICE_IMG[s.id]}
              eyebrow={s.eyebrow}
              title={s.title}
              text={s.text}
              cta={s.cta}
              aspect="aspect-[16/10]"
              as="h2"
            />
          ))}
        </div>
      </section>

      <section data-testid="services-more" className="bg-stone-100 border-y border-stone-200 py-14 sm:py-28 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <p className="eyebrow">{sp.moreEyebrow}</p>
          <h2 className="font-serif mt-6 text-base md:text-lg text-stone-900">{sp.moreTitle}</h2>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-4">
            {sp.moreLinks.map((l) => (
              <Link
                key={l.to}
                data-testid={`services-more-${l.to.replace(/\//g, "")}`}
                to={l.to}
                className="group inline-flex items-center gap-2 text-stone-700 hover:text-stone-900 border-b border-stone-200 hover:border-stone-900 py-3 transition-colors"
              >
                {l.label}
                <ArrowRight size={13} className="opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="services-cta" className="py-16 sm:py-32 px-6 lg:px-10 text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-serif text-4xl sm:text-5xl font-light text-stone-900 italic">{t.finalCta.title}</h2>
          <p className="mt-6 text-stone-600 text-lg">{t.finalCta.text}</p>
          <Link
            data-testid="services-cta-contact"
            to="/contact"
            className="mt-10 inline-flex items-center justify-center gap-3 bg-slate-900 text-stone-50 px-8 py-4 text-sm font-medium hover:bg-slate-800 transition-colors"
          >
            {t.finalCta.cta}
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </PageShell>
  );
}
